import { NgFor, NgIf } from '@angular/common';
import { Component, NgZone, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { MapLoaderService } from '../services/map-loader.service';


interface BranchLocation {
  branch: string;
  branchKh: string;
  tel: string;
  address: string;
  addressKh: string;
  position: google.maps.LatLngLiteral
}

@Component({
  selector: 'app-side-map',
  standalone: true,
  imports: [GoogleMap, MapMarker, NgFor, NgIf, GoogleMapsModule, TranslateModule],
  templateUrl: './site-map.component.html',
  styleUrl: './site-map.component.css'
})
export class SiteMapComponent {

  isMapReady = false;

  googleMapsApiKey = environment.googleMapsApiKey;

  baseApiUrl = environment.baseApiUrl;

  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  center: google.maps.LatLngLiteral = { lat: 12.3798, lng: 104.9317 };
  zoom = 7;

  markers: { position: google.maps.LatLngLiteral; title: string, desc: string }[] = [];

  currentLanguage: string = '';
  
  constructor(private translate: TranslateService, private mapLoader: MapLoaderService) {}
    ngOnInit(): void {
      this.currentLanguage = this.translate.currentLang || this.translate.defaultLang;
      this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })

      this.mapLoader.isLoaded$.subscribe((loaded) => {
        this.isMapReady = loaded;
      });
  }

  branchLocations: BranchLocation[] = [
    {
      branch: 'Head Office (HO)',
      branchKh: 'ការិយាល័យកណ្តាល​ (HO)',
      tel: '023 998 226 | 023 996 023',
      address: 'Building N388A, E1, E2, E3 Preah Monivong Blvd. Corner Street N352, Sangkat Boeng Keng Kang Ti Mouy, Khan Boeng Keng Kang, Phnom Penh',
      addressKh: 'អគារលេខ៣៨៨A E1, E2, E3, មហាវិថីព្រះមុនីវង្ស កែងផ្លូវលេខ៣៥២ សង្កាត់បឹងកេងកង១ ខណ្ឌបឹងកេងកង រាជធានីភ្នំពេញ',
      position: { lat: 11.5488, lng: 104.9219 }
    },
    {
      branch: 'Kandal Provincial Branch (KD)',
      branchKh: 'សាខាខេត្តកណ្តាល (KD)',
      tel: '071 336 66 80 | 071 336 66 86',
      address: 'N8A1, National Road 4, Svay Chrum village, Baek Chan commune, Angk Snuol district, Kandal province',
      addressKh: 'ផ្ទះលេខ៨A1 ផ្លូវជាតិលេខ៤ ភូមិស្វាយជ្រំ ឃុំបែកចាន ស្រុកអង្គស្នួល ខេត្តកណ្តាល',
      position: { lat: 11.5098, lng: 104.7432 }
    },
    {
      branch: 'S’ang District Branch (SA)',
      branchKh: 'សាខាស្រុកស្អាង (SA)',
      tel: '071 33 66 690 | 071 33 66 691',
      address: 'National Road No.21, Preaek Run village, Preaek Koy commune, S’ang district, Kandal province',
      addressKh: 'ផ្លូវជាតិលេខ២១ ភូមិព្រែករុន ឃុំព្រែកគយ ស្រុកស្អាង ខេត្តកណ្តាល',
      position: { lat: 11.3596, lng: 105.0060  }
    },
    {
      branch: 'Kampong Speu Provincial Branch (KS)',
      branchKh: 'សាខាខេត្តកំពង់ស្ពឺ (KS)',
      tel: '071 33 66 760 | 071 33 22 063',
      address: 'National Road No.4, Peanicheakkam village Sangkat Rokar Thum, Krong Chbar Mon, Kampong Speu province',
      addressKh: 'ផ្លូវជាតិលេខ៤​ ភូមិពាណិជ្ជកម្ម សង្កាត់រការធំ ក្រុងច្បារមន ខេត្តកំពង់ស្ពឺ',
      position: { lat: 11.4597, lng: 104.5116  }
    },
    {
      branch: 'Takeo Provincial Branch (TK)',
      branchKh: 'សាខាខេត្ត តាកែវ (TK)',
      tel: '071 33 66 740 | 071 33 66 741',
      address: 'Angk Ta Saom village, Angk Ta Saom commune, Tram Kak district, Takeo province',
      addressKh: 'ភូមិអង្គតាសោម ឃុំអង្គតាសោម ស្រុកត្រាំកក់ ខេត្តតាកែវ',
      position: { lat: 11.0098, lng: 104.6718  }
    },
    {
      branch: 'Chhuk District Branch (CK)',
      branchKh: 'សាខាស្រុកឈូក (CK)',
      tel: '071 33 66 710 | 071 55 58 346',
      address: 'National Road No.3, Chheu Teal Village, Chhuk commune, Chhuk district, Kampot province',
      addressKh: 'ផ្លូវជាតិលេខ៣ ភូមិឈើទាល ឃុំឈូក ស្រុកឈូក ខេត្តកំពត',
      position: { lat: 10.8378, lng: 104.4577 }
    },
    {
      branch: 'Banteay Meas District Branch (BM)',
      branchKh: 'សាខាស្រុកបន្ទាយមាស (BM)',
      tel: '071 33 66 640 | 071 33 66 641',
      address: 'Tuk Meas village, Tuk Meas Khang Lech commune, Banteay Meas district, Kampot province',
      addressKh: 'ភូមិទូកមាស ឃុំទូកមាសខាងលិច ស្រុកបន្ទាយមាស ខេត្តកំពត',
      position: { lat: 10.6653, lng: 104.5578 }
    },
    {
      branch: 'Kampong Trach District Branch (KT)',
      branchKh: 'សាខាស្រុកកំពង់ត្រាច (KT)',
      tel: '071 33 66 610 | 071 33 66 604',
      address: 'KampongTrach Ti Mouy village, Kampong Trach Khang Kaeut Commune, Kampong Trach district, Kampot province',
      addressKh: 'ភូមិកំពង់ត្រាចទី១ ឃុំកំពង់ត្រាចខាងកើត ស្រុកកំពង់ត្រាច ខេត្តកំពត',
      position: { lat: 10.5591, lng: 104.4641 }
    },
    {
      branch: 'Kampot Provincial Branch (KP)',
      branchKh: 'សាខាខេត្តកំពត (KP)',
      tel: '071 33 66 603 | 071 33 66 717',
      address: 'Phum Kampong Bay Khang Tboung, Sangkat Kampong Bay, Krong Kampot, Kampot province',
      addressKh: 'ភូមិកំពង់បាយខាងត្បូង សង្កាត់កំពង់បាយ ស្រុកកំពត ខេត្តកំពត',
      position: { lat: 10.6128, lng: 104.1818 }
    },
    {
      branch: 'Kep Provincial Branch (Kep)',
      branchKh: 'សាខាខេត្តកែប (Kep)',
      tel: '071 33 66 623 | 071 33 66 624',
      address: 'National Road No.33, Damnak Chang’aeur village, Sangkat Prey Thum, Krong Kep, Kep province',
      addressKh: 'ផ្លូវជាតិលេខ៣៣ ភូមិដំណាក់ចង្អើរ សង្កាត់ព្រៃធំ ក្រុងកែប ខេត្តកែប',
      position: { lat: 10.5388, lng: 104.3234 }
    },
    {
      branch: 'Prey Nob District Branch (PN)',
      branchKh: 'សាខាស្រុកព្រៃនប់ (PN)',
      tel: '071 33 66 670 | 071 33 66 671',
      address: 'National Road No.4, Veal Meas village, Veal Renh commune, Prey Nob district, Preah Sihanouk province',
      addressKh: 'ផ្លូវជាតិលេខ៤ ភូមិវាលមាស ឃុំវាលរេញ ស្រុកព្រៃនប់ ខេត្តព្រះសីហនុ',
      position: { lat: 10.7076, lng: 103.8170 }
    },
    {
      branch: 'Preah Sihanouk Provincial Branch (PSN)',
      branchKh: 'សាខាខេត្តព្រះសីហនុ (PSN)',
      tel: '071 33 66 660 | 071 33 66 662',
      address: 'Phum Bei, Sangkat Bei, Krong Preah Sihanouk, Preah Sihanouk province',
      addressKh: 'ភូមិ៣ សង្កាត់៣ ក្រុងព្រះសីហនុ ខេត្តព្រះសីហនុ',
      position: { lat: 10.6352, lng: 103.5121 }
    },
    {
      branch: 'Stueng Hav District Branch (STH)',
      branchKh: 'សាខាស្រុកស្ទឹងហាវ (STH)',
      tel: '071 33 66 730 | 071 33 66 731',
      address: 'Phum Bei, Tomnob Rolok commune, Stueng Hav district, Preah Sihanouk province',
      addressKh: 'ភូមិ៣ ឃុំទំនប់រលក ស្រុកស្ទឹងហាវ ខេត្តព្រះសីហនុ។',
      position: { lat: 10.7355, lng: 103.6243 }
    },
    {
      branch: 'Baray District Branch (BR)',
      branchKh: 'សាខាស្រុកបារាយណ៍ (BR)',
      tel: '071 33​ 66 697 | 071 33 66 752',
      address: 'Prey Ta Trav village, Ballangk commune, Baray district, Kampong Thom province',
      addressKh: 'ភូមិព្រៃតាត្រាវ ឃុំបល្ល័ង្គ ស្រុកបារាយណ៍ ខេត្កកំពង់ធំ',
      position: { lat: 12.4975, lng: 105.1242 }
    },
    {
      branch: 'Kampong Thom Provincial Branch (KTH)',
      branchKh: 'សាខាខេត្តកំពង់ធំ (KTH)',
      tel: '071 33 66 750 | 071 33 66 751',
      address: 'Kdei village, Sangkat Prey Ta Hu, Krong Stueng Saen, Kampong Thom Province',
      addressKh: 'ភូមិក្តី សង្កាត់ព្រៃតាហ៊ូ ក្រុងស្ទឹងសែន ខេត្តកំពង់ធំ',
      position: { lat: 12.7191, lng: 104.8865 }
    },
    {
      branch: 'Puok District Branch (PK)',
      branchKh: 'សាខាស្រុកពួក (PK)',
      tel: '071 33 66 720 | 071 33 66 721',
      address: 'No.1228, Kouk Chuon village, Puok commune, Puok district, Siemreap province',
      addressKh: 'ផ្ទះលេខ ១២២៨ ភូមិគោកជួន ឃុំពួក ស្រុកពួក ខេត្តសៀមរាប',
      position: { lat: 13.4411, lng: 103.7292 }
    },
    {
      branch: 'Siemreap Provincial Branch (SR)',
      branchKh: 'សាខាខេត្តសៀមរាប (SR)',
      tel: '071 33 66 650 | 071 33 66 652',
      address: 'Banteay Chas village, Sangkat Sla Kram, Krong Siemreap, Siemreap province',
      addressKh: 'ភូមិបន្ទាយចាស់ សង្កាត់ស្លក្រាម ក្រុងសៀមរាប ខេត្តសៀមរាប',
      position: { lat: 13.3611, lng: 103.8716 }
    },
    {
      branch: 'Prey​ Veng Provincial Branch (PV)',
      branchKh: 'សាខាខេត្តព្រៃវែង (PV)',
      tel: '071 33 30 116 | 071 33 30 117',
      address: 'No.345, Prasat village, Prasat commune, Kampong Trabaek district, Prey Veng province',
      addressKh: 'ផ្ទះលេខ៣៤៥ ភូមិប្រាសាទ ឃុំប្រាសាទ ស្រុកកំពង់ត្របែក ខេត្តព្រៃវែង',
      position: { lat: 11.1476,	lng: 105.5385 }
    },
    {
      branch: 'Svay Rieng Provincial Branch (SVR)',
      branchKh: 'សាខាខេត្តស្វាយរៀង (SVR)',
      tel: '071 41 88 333 | 071 55 59 449',
      address: 'Chong Preaek village, Sangkat Svay Rieng, Krong, Krong Svay Rieng, Svay Rieng province',
      addressKh: 'ភូមិ​ចុងព្រែក សង្កាត់ស្វាយរៀង​ ក្រុង​ស្វាយរៀង​ ខេត្តស្វាយរៀង​',
      position: { lat: 11.0917, lng: 105.7900 }
    },
    {
      branch: 'Kampong Cham Provincial Branch (KPC)',
      branchKh: 'សាខាខេត្តកំពង់ចាម​ (KPC)',
      tel: '071 33 36 328 | 031 42 32 333',
      address: 'National Road No.7, Prey Totueng village, Chrey Vien commune, Prey Chhor district, Kampong cham province',
      addressKh: 'ផ្លូវជាតិលេខ៧ ភូមិព្រៃទទឹង ឃុំជ្រៃវៀន ស្រុកព្រៃឈរ ខេត្តកំពង់ចាម',
      position: { lat: 12.0583, lng: 105.2559 }
    },
    {
      branch: 'Chbar Ampov Branch (CBA)',
      branchKh: 'សាខាច្បារអំពៅ(CBA)',
      tel: '071 33 36 302 | 071 33 39 276',
      address: 'No.578A,E0-E3,National Road No.1, Deum Slaeng village, Sangkat Chbar Ampov Ti Pir, Khan Chbar Ampov, Phnom Penh',
      addressKh: 'ផ្ទះលេខ៥៧៨A E0-E3 ផ្លូវជាតិលេខ១ ភូមិដើមស្លែង សង្កាត់ច្បារអំពៅទី២ ខណ្ឌច្បារអំពៅ រាជធានីភ្នំពេញ',
      position: { lat: 11.5319, lng: 104.9386 }
    },
    {
      branch: 'Krong Bavet Branch (BV)',
      branchKh: 'សាខាក្រុងបារិត (BV)',
      tel: '071 33 30 118 | 071 33 30 119',
      address: 'Tuol Ampel village, Sangkat Chrak Mtes, Krong Bavet, Svay Rieng province',
      addressKh: 'ភូមិទួលអំពិល សង្កាត់ច្រកម្ទេស ក្រុងបាវិត ខេត្តស្វាយរៀង',
      position: { lat: 11.0380, lng: 106.0106 }
    },
    {
      branch: 'BATI District Branch (BT)',
      branchKh: 'សាខាស្រុកបាទី(BT)',
      tel: '071 41 80 333 | 071 41 10 909',
      address: 'National Road No.2, Smau Khnhei village, Trapeang Sab commune, Bati district, Takeo province',
      addressKh: 'ផ្លូវជាតិលេខ២ ភូមិស្មៅខ្ញី ឃុំត្រពាំងសាប ស្រុកបាទី ខេត្តតាកែវ',
      position: { lat: 11.2645, lng: 104.8250 }
    },
    {
      branch: 'Khan​ Boeng Keng Kang Branch (BKK)',
      branchKh: 'សាខាខណ្ឌបឹងកេងកង(BKK)',
      tel: '071 41 12 526 | 071 41 12 406',
      address: 'Building No.388A E0, Preah Monivong Blvd. Corner Street No.352, Sangkat Boeng Keng Kang Ti Muoy, Khan Boeng Keng Kang, Phnom Penh',
      addressKh: 'អគារលេខ​ ៣៨៨A E0 មហាវិថីព្រះមុនីវង្សកែងផ្លូវ ៣៥២ សង្កាត់បឹងកេងកង១ ខណ្ឌបឹងកេងកង រាជធានីភ្នំពេញ',
      position: { lat: 11.5487, lng: 104.9218 }
    },
    {
      branch: 'Odongk District Branch (OD)',
      branchKh: 'សាខាស្រុកឧដុង្គ(OD)',
      tel: '071 41 12 788 | 071 41 12 028',
      address: 'OKarong village, Veang Chas commune, Odongk district, Kampong Speu province',
      addressKh: 'ភូមិអូរការុង ឃុំវាំងចាស់ ស្រុកឧដុង្គ ខេត្តកំពង់ស្ពឺ',
      position: { lat: 11.8240, lng: 104.7407 }
    },
  ];

  selectedBranch: any = null;

  onMapClick(event:google.maps.MapMouseEvent):void {
    // if(event.latLng){
    //   const position = event.latLng.toJSON()

    //   this.markers.push({
    //     position,
    //     title: `Marker at (${position.lat}, ${position.lng})`
    //   })
    // }
    this.infoWindow.close();
  }

  onMarkerClick(branch: any, marker: MapMarker): void {
    this.selectedBranch = branch;
    this.infoWindow.open(marker);
  
  }
}
